import React from 'react';
import {
  lighten,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PaiActionIconText from 'features/ui/base-components/pai-action-icon-text';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';
import { Contact } from '../contact-management.types';

/** Safely format a possibly missing/invalid date; never throws on an Invalid Date. */
const formatDate = (value?: Date | string | number | null): string => {
  if (value === undefined || value === null || value === '') return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return formatDateTime(date.toISOString(), 'Pp');
};

interface Props {
  contact: Contact | undefined;
  handleOpenEditContactNameDialog(): void;
  handleOpenEditGenderDialog(): void;
  handleOpenEditManualTagsDialog(): void;
  handleOpenEditContactEmailDialog(): void;
}

const ContactInfoTable: React.FC<Props> = ({
  contact,
  handleOpenEditContactNameDialog,
  handleOpenEditGenderDialog,
  handleOpenEditManualTagsDialog,
  handleOpenEditContactEmailDialog
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  let fullName = '';
  if (contact?.firstname) {
    fullName += contact.firstname;
  }
  if (contact?.lastname) {
    if (contact.firstname) {
      fullName += ' ';
    }
    fullName += contact.lastname;
  }

  return (
    <>
      <TableContainer className={classes.container}>
        <Table className={classes.table} size="small">
          <TableBody>
            {/* Contact Name */}
            <TableRow>
              <TableCell>
                <Stack direction="row" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography className={classes.text} fontWeight={500}>
                      {t('contactName')}:
                    </Typography>
                    <PaiActionIconText
                      variant="edit"
                      dense={true}
                      action={handleOpenEditContactNameDialog}
                      disabled={_.isNil(contact)}
                    />
                  </Stack>
                  <Typography className={classes.text} align="right">
                    {fullName || '-'}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>

            {/* Gender */}
            <TableRow>
              <TableCell>
                <Stack direction="row" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography className={classes.text} fontWeight={500} whiteSpace="nowrap">
                      {t('contactGender')}:
                    </Typography>
                    <PaiActionIconText
                      variant="edit"
                      dense={true}
                      action={handleOpenEditGenderDialog}
                      disabled={_.isNil(contact)}
                    />
                  </Stack>
                  <Typography className={classes.text} align="right">
                    {contact?.sex
                      ? contact.sex === 'male'
                        ? t('maleGender')
                        : contact.sex === 'female'
                          ? t('femaleGender')
                          : t('uncertainSex')
                      : '-'}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>

            {/* Occupation */}
            <TableRow>
              <TableCell>
                <Stack direction="row" justifyContent="space-between">
                  <Typography className={classes.text} fontWeight={500} whiteSpace="nowrap">
                    {t('position')}:
                  </Typography>
                  <Typography className={classes.text} align="right">
                    {contact?.occupation || '-'}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>

            {/* Polite speech */}
            <TableRow>
              <TableCell>
                <Stack direction="row" justifyContent="space-between">
                  <Typography className={classes.text} fontWeight={500} whiteSpace="nowrap">
                    {t('politeSpeech')}:
                  </Typography>
                  <Typography className={classes.text} align="right">
                    {_.isNil(contact?.isPolite)
                      ? '-'
                      : contact?.isPolite
                        ? t('formal')
                        : t('informal')}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>

            {/* Email */}
            <TableRow>
              <TableCell>
                <Stack direction="row" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography className={classes.text} fontWeight={500}>
                      {t('email')}:
                    </Typography>
                    <PaiActionIconText
                      variant="edit"
                      dense={true}
                      action={handleOpenEditContactEmailDialog}
                      disabled={_.isNil(contact)}
                    />
                  </Stack>
                  <Typography className={classes.text} align="right">
                    {contact?.email || '-'}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>

            {/* LinkedIn Profile */}
            <TableRow>
              <TableCell>
                <Stack direction="row" justifyContent="space-between">
                  <Typography className={classes.text} fontWeight={500} whiteSpace="nowrap">
                    {t('linkedinProfile')}:
                  </Typography>
                  {!_.isNil(contact) ? (
                    <PaiLink
                      text={_.truncate(contact.profileId, { length: 40 })}
                      size="md"
                      variant="primary"
                      underline="always"
                      href={contact.profileId}
                      target="_blank"
                    />
                  ) : (
                    '-'
                  )}
                </Stack>
              </TableCell>
            </TableRow>

            {/* Company */}
            <TableRow>
              <TableCell>
                <Stack direction="row" justifyContent="space-between">
                  <Typography className={classes.text} fontWeight={500} whiteSpace="nowrap">
                    {t('company')}:
                  </Typography>
                  <Typography className={classes.text} align="right">
                    {contact?.companyName ?? '-'}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>

            {/* Contact Persons */}
            <TableRow>
              <TableCell>
                <Typography className={classes.text} fontWeight={500} whiteSpace="nowrap">
                  {t('contactPersons')}:
                </Typography>
              </TableCell>
            </TableRow>
            {!_.isEmpty(contact?.contactPersons) && (
              <TableRow>
                <TableCell style={{ padding: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow className={classes.tableHeadRow}>
                        <TableCell style={{ width: '50%' }}>{t('name')}</TableCell>
                        <TableCell style={{ width: '50%' }} align="right">
                          {t('contactDegree')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contact?.contactPersons
                        ?.filter((person) => person.contactDegree)
                        ?.map((person, index) => (
                          <TableRow key={index}>
                            <TableCell>{person.fullName}</TableCell>
                            <TableCell align="right">{person.contactDegree}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableCell>
              </TableRow>
            )}

            {/* First Contact On */}
            <TableRow>
              <TableCell>
                <Stack direction="row" justifyContent="space-between">
                  <Typography className={classes.text} fontWeight={500} whiteSpace="nowrap">
                    {t('firstContactOn')}:
                  </Typography>
                  <Typography className={classes.text} align="right">
                    {formatDate(contact?.createDate)}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>

            {/* Manual Tags */}
            <TableRow>
              <TableCell>
                <Stack direction="row" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography className={classes.text} fontWeight={500} whiteSpace="nowrap">
                      {t('manualTags')}:
                    </Typography>
                    <PaiActionIconText
                      variant="edit"
                      dense={true}
                      action={handleOpenEditManualTagsDialog}
                      disabled={_.isNil(contact)}
                    />
                  </Stack>
                  <Typography className={classes.text} align="right">
                    {contact?.manualTags?.map((tag) => tag.name).join(', ') ?? '-'}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    borderRadius: '12px',
    background: '#FFF',
    overflow: 'hidden'
  },
  table: {
    '& th': {
      border: 'none',
      padding: '4px 24px',
      fontSize: theme.typography.pxToRem(13),
      '&:first-of-type': {
        borderTopLeftRadius: '6px',
        borderBottomLeftRadius: '6px'
      },
      '&:last-of-type': {
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px'
      }
    },
    '& td, th': {
      border: 'none',
      borderBottom: '1px solid #ddd',
      padding: '8px 24px',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13)
    },
    '& mark': {
      backgroundColor: lighten(theme.palette.secondary.main, 0.8)
    }
  },
  tableHeadRow: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFF',
    zIndex: 9
  },
  text: {
    fontSize: theme.typography.pxToRem(13)
  }
}));

export default ContactInfoTable;
